"use client";
import React from "react";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const features = [
  "User-friendly module interface",
  "Secure exam registration, management, and delivery",
  "AI and biometric interfaces for proctoring, surveillance, and question paper generation",
  "Foolproof encryption algorithms to prevent data leaks",
  "Digitization and scanning of answer books & OMR sheets",
  "User-friendly evaluation automation (home-based, centre-based, or blended)",
  "Customizable results processing & analytics dashboard",
  "Cloud-based scalability and high availability",
  "API integrations with existing examination systems",
  "Compliance with government data security guidelines",
];

const advantages = [
  "Reduced administrative burden",
  "Cost savings on logistics and traditional processes",
  "Faster results processing",
  "Enhanced productivity and decision-making",
  "Transparency and good governance",
];

const Onex = () => {
  const location = useLocation();
  return (
    <div className={`bg-white text-dark mr-50 ml-50`}>
      {/* Header Section */}
      <section className=" py-5">
        <motion.h1
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fs-3 fw-bold text-primary"
        >
          End-to-End Examination Solutions
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-4 fs-5 text-secondary"
        >
          Revolutionizing examinations with a seamless, secure, and scalable
          digital platform. OneX eliminates the hassles of traditional exams by
          offering{" "}
          <span className="fw-semibold text-primary">
            pre-exam, exam, and post-exam automation
          </span>
          , including question paper management, AI-powered remote proctoring,
          online exam delivery, answer book digitization, and advanced results
          analytics.
        </motion.p>
      </section>

      {/* Solution Preface */}
      <section className="">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-info bg-opacity-10 border-start border-4 border-info rounded shadow-sm p-4"
        >
          <h3 className="h4 fw-semibold text-info mb-3">Solution Preface</h3>
          <p className="text-black mb-3">
            Our OneX solution is designed for certification bodies seeking
            security, scalability, and efficiency. With robust encryption,
            biometric authentication, and multi-format exam support, OneX
            ensures transparency and integrity at every stage.
          </p>
          <p className="text-black">
            Supporting both online and offline exam modes, it integrates
            seamlessly with existing systems through APIs while offering
            advanced insights into candidate performance.
          </p>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className=" py-3">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="h2 fw-bold text-primary text-center mb-5"
        >
          Key Features
        </motion.h2>

        <div className="row g-4">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="col-12 col-sm-6 col-md-4 mt-2"
            >
              <div className="bg-white rounded shadow-sm  p-3 d-flex gap-2 align-items-start hover-shadow">
                <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                <span className="text-black ">{feature}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key Advantages */}
      <section className=" text-white py-5" style={{ backgroundColor: "#0a1530" }}>
        <div className="container text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="h2 fw-bold"
          >
            Key Advantages
          </motion.h2>
          <div className="row g-4 mt-4 text-start">
            {advantages.map((adv, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="col-12 col-sm-6 col-md-4 mt-2"
              >
                <div className="bg-white bg-opacity-25 p-3 rounded d-flex gap-2 align-items-start">
                  <FaCheckCircle className="text-success mt-1" />
                  <span className="text-white">{adv}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="container py-5">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-white rounded shadow-sm p-4"
        >
          <p className="text-black">
            With customizable workflows, cloud reliability, and compliance with
            government security guidelines, OneX ensures future-proof
            examination management. By blending automation with human oversight,
            it delivers accuracy, fairness, and unmatched scalability.
          </p>
        </motion.div>
      </section>
    </div>
  );
};

export default Onex;
